import React, { useEffect, useState } from 'react';
import client from '../../api/client';
import Navbar from '../../components/Navbar';
import { useAuth } from '../../context/AuthContext';

const STATUS_COLOR = {
  active:      { background: '#d4edda', color: '#155724' },
  complete:    { background: '#cce5ff', color: '#004085' },
  paused:      { background: '#fff3cd', color: '#856404' },
  available:   { background: '#f8f9fa', color: '#495057' },
  in_progress: { background: '#fff3cd', color: '#856404' },
  submitted:   { background: '#cce5ff', color: '#004085' },
  approved:    { background: '#d4edda', color: '#155724' },
  rejected:    { background: '#f8d7da', color: '#721c24' },
};

export default function AdminOverview() {
  const { user } = useAuth();
  const [tab, setTab] = useState('projects');

  // ── Projects ──────────────────────────────────────────────────────────────
  const [projects, setProjects] = useState([]);
  const [projLoading, setProjLoading] = useState(true);
  const [deletingProjectId, setDeletingProjectId] = useState(null);

  const [expandedProject, setExpandedProject] = useState(null);
  const [tasksByProject, setTasksByProject] = useState({});
  const [tasksLoading, setTasksLoading] = useState(false);

  const [workers, setWorkers] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [assigning, setAssigning] = useState({});
  const [selections, setSelections] = useState({});

  // ── Users ──────────────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    client.get('/api/projects/')
      .then(res => setProjects(res.data))
      .finally(() => setProjLoading(false));

    client.get('/api/users/list/?role=annotator').then(res => setWorkers(res.data));
    client.get('/api/users/list/?role=reviewer').then(res => setReviewers(res.data));
  }, []);

  function loadUsers() {
    setUsersLoading(true);
    client.get('/api/admin/users/')
      .then(res => setUsers(res.data))
      .finally(() => setUsersLoading(false));
  }

  useEffect(() => {
    if (tab === 'users') loadUsers();
  }, [tab]);

  // ── Project actions ────────────────────────────────────────────────────────
  function toggleProject(projectId) {
    if (expandedProject === projectId) { setExpandedProject(null); return; }
    setExpandedProject(projectId);
    if (tasksByProject[projectId]) return;
    setTasksLoading(true);
    client.get(`/api/tasks/?project=${projectId}`)
      .then(res => setTasksByProject(prev => ({ ...prev, [projectId]: res.data })))
      .finally(() => setTasksLoading(false));
  }

  async function handleDeleteProject(p) {
    if (!window.confirm(`Delete project "${p.name}"? This cannot be undone.`)) return;
    setDeletingProjectId(p.id);
    try {
      await client.delete(`/api/projects/${p.id}/`);
      setProjects(prev => prev.filter(x => x.id !== p.id));
      if (expandedProject === p.id) setExpandedProject(null);
    } catch {
      alert('Failed to delete project.');
    } finally {
      setDeletingProjectId(null);
    }
  }

  function setSelection(taskId, field, value) {
    setSelections(prev => ({ ...prev, [taskId]: { ...(prev[taskId] || {}), [field]: value } }));
  }

  async function assignTask(task) {
    const sel = selections[task.id] || {};
    const body = {};
    if (sel.worker_id) body.worker_id = sel.worker_id;
    if (sel.reviewer_id) body.reviewer_id = sel.reviewer_id;
    if (!Object.keys(body).length) return;

    setAssigning(prev => ({ ...prev, [task.id]: true }));
    try {
      const res = await client.patch(`/api/tasks/${task.id}/assign/`, body);
      const updated = res.data;
      setTasksByProject(prev => ({
        ...prev,
        [task.project_id]: prev[task.project_id].map(t => t.id === updated.id ? updated : t),
      }));
      setSelections(prev => { const n = { ...prev }; delete n[task.id]; return n; });
    } catch {
      alert('Assignment failed.');
    } finally {
      setAssigning(prev => ({ ...prev, [task.id]: false }));
    }
  }

  // ── User actions ───────────────────────────────────────────────────────────
  async function handleApprove(userId) {
    setActionLoading(prev => ({ ...prev, [`approve-${userId}`]: true }));
    try {
      await client.post(`/api/admin/users/${userId}/approve/`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_approved: true } : u));
    } catch {
      alert('Failed to approve user.');
    } finally {
      setActionLoading(prev => ({ ...prev, [`approve-${userId}`]: false }));
    }
  }

  async function handleWhitelist(userId) {
    setActionLoading(prev => ({ ...prev, [`whitelist-${userId}`]: true }));
    try {
      await client.post(`/api/admin/users/${userId}/whitelist/`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_whitelisted: true } : u));
    } catch {
      alert('Failed to whitelist user.');
    } finally {
      setActionLoading(prev => ({ ...prev, [`whitelist-${userId}`]: false }));
    }
  }

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.content}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Admin Overview</h1>
            <p style={styles.sub}>Welcome, <strong>{user?.name}</strong></p>
          </div>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          {['projects', 'users', 'stats'].map(t => (
            <button
              key={t}
              style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
              onClick={() => setTab(t)}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Stats tab */}
        {tab === 'stats' && (
          <div style={styles.statsGrid}>
            <StatCard label="Total Projects" value={projects.length} />
            <StatCard label="Active" value={projects.filter(p => p.status === 'active').length} />
            <StatCard label="Complete" value={projects.filter(p => p.status === 'complete').length} />
          </div>
        )}

        {/* Users tab */}
        {tab === 'users' && (
          <>
            {usersLoading && <p style={styles.hint}>Loading users…</p>}
            {!usersLoading && users.length === 0 && <p style={styles.hint}>No users found.</p>}
            {users.length > 0 && (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {['Email', 'Role', 'Trial Used', 'Whitelisted', 'Approved', 'Actions'].map(h => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} style={styles.row}>
                        <td style={styles.td}>{u.email}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, ...roleBadgeColor(u.role) }}>{u.role}</span>
                        </td>
                        <td style={styles.tdCenter}>{u.free_trial_used ? '✓' : '—'}</td>
                        <td style={styles.tdCenter}>{u.is_whitelisted ? '✓' : '—'}</td>
                        <td style={styles.tdCenter}>{u.is_approved ? '✓' : '—'}</td>
                        <td style={styles.td}>
                          <div style={styles.actionRow}>
                            {!u.is_approved && (
                              <button
                                style={styles.approveBtn}
                                disabled={!!actionLoading[`approve-${u.id}`]}
                                onClick={() => handleApprove(u.id)}
                              >
                                {actionLoading[`approve-${u.id}`] ? '…' : 'Approve'}
                              </button>
                            )}
                            {!u.is_whitelisted && (
                              <button
                                style={styles.whitelistBtn}
                                disabled={!!actionLoading[`whitelist-${u.id}`]}
                                onClick={() => handleWhitelist(u.id)}
                              >
                                {actionLoading[`whitelist-${u.id}`] ? '…' : 'Whitelist'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Projects tab */}
        {tab === 'projects' && (
          <>
            {projLoading && <p style={styles.hint}>Loading projects…</p>}
            {!projLoading && projects.length === 0 && <p style={styles.hint}>No projects yet.</p>}

            {projects.map(p => (
              <div key={p.id} style={styles.projectCard}>
                <div style={styles.projectRow}>
                  <div style={styles.projectInfo} onClick={() => !p.is_done && toggleProject(p.id)}>
                    <span style={styles.projectName}>{p.name}</span>
                    <span style={styles.projectMeta}>Client: {p.client_email || p.client_name}</span>
                    <span style={styles.projectMeta}>{p.image_count} images · {p.labeled_count} labeled</span>
                  </div>
                  <div style={styles.projectRight}>
                    {p.is_done ? (
                      <span style={{ ...styles.badge, ...styles.badgeDone }}>Done</span>
                    ) : (
                      <span style={{ ...styles.badge, ...(STATUS_COLOR[p.status] || {}) }}>{p.status}</span>
                    )}
                    {!p.is_done && (
                      <span style={styles.expandBtn} onClick={() => toggleProject(p.id)}>
                        {expandedProject === p.id ? '▲ Hide Tasks' : '▼ Assign Tasks'}
                      </span>
                    )}
                    <button
                      style={styles.deleteBtn}
                      disabled={deletingProjectId === p.id}
                      onClick={() => handleDeleteProject(p)}
                    >
                      {deletingProjectId === p.id ? '…' : 'Delete'}
                    </button>
                  </div>
                </div>

                {/* Tasks panel — only for non-done projects */}
                {!p.is_done && expandedProject === p.id && (
                  <div style={styles.taskPanel}>
                    {tasksLoading && !tasksByProject[p.id] && <p style={styles.hint}>Loading tasks…</p>}
                    {tasksByProject[p.id] && tasksByProject[p.id].length === 0 && (
                      <p style={styles.hint}>No tasks for this project.</p>
                    )}
                    {tasksByProject[p.id] && tasksByProject[p.id].length > 0 && (
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            {['Image', 'Status', 'Assign Worker', 'Assign Reviewer', ''].map(h => (
                              <th key={h} style={styles.th}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {tasksByProject[p.id].map(t => {
                            const sel = selections[t.id] || {};
                            return (
                              <tr key={t.id} style={styles.row}>
                                <td style={styles.td}>{t.image_filename}</td>
                                <td style={styles.td}>
                                  <span style={{ ...styles.badge, ...(STATUS_COLOR[t.status] || {}) }}>
                                    {t.status.replace('_', ' ')}
                                  </span>
                                </td>
                                <td style={styles.td}>
                                  <select
                                    style={styles.select}
                                    value={sel.worker_id || t.worker_id || ''}
                                    onChange={e => setSelection(t.id, 'worker_id', e.target.value)}
                                  >
                                    <option value="">{t.worker_name || '— unassigned —'}</option>
                                    {workers.map(w => (
                                      <option key={w.id} value={w.id}>{w.name}</option>
                                    ))}
                                  </select>
                                </td>
                                <td style={styles.td}>
                                  <select
                                    style={styles.select}
                                    value={sel.reviewer_id || t.reviewer_id || ''}
                                    onChange={e => setSelection(t.id, 'reviewer_id', e.target.value)}
                                  >
                                    <option value="">{t.reviewer_name || '— unassigned —'}</option>
                                    {reviewers.map(r => (
                                      <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                  </select>
                                </td>
                                <td style={styles.td}>
                                  <button
                                    style={styles.assignBtn}
                                    disabled={assigning[t.id] || !selections[t.id]}
                                    onClick={() => assignTask(t)}
                                  >
                                    {assigning[t.id] ? '…' : 'Assign'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statValue}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  );
}

function roleBadgeColor(role) {
  if (role === 'admin')     return { background: '#f8d7da', color: '#721c24' };
  if (role === 'client')    return { background: '#cce5ff', color: '#004085' };
  if (role === 'annotator') return { background: '#d4edda', color: '#155724' };
  return { background: '#fff3cd', color: '#856404' };
}

const styles = {
  page: { minHeight: '100vh', background: '#f0f2f5', fontFamily: 'sans-serif' },
  content: { padding: '2rem', maxWidth: '1100px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' },
  title: { margin: 0, fontSize: '1.6rem', color: '#2c3e50' },
  sub: { margin: '0.25rem 0 0', color: '#555' },
  hint: { color: '#888' },
  tabs: { display: 'flex', gap: '0.25rem', borderBottom: '2px solid #ddd', marginBottom: '1.5rem' },
  tab: { background: 'none', border: 'none', padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.95rem', color: '#888', fontWeight: 500, borderBottom: '2px solid transparent', marginBottom: '-2px' },
  tabActive: { color: '#2c3e50', borderBottom: '2px solid #2c3e50' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' },
  statCard: { background: '#fff', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', textAlign: 'center' },
  statValue: { fontSize: '2rem', fontWeight: 700, color: '#2c3e50' },
  statLabel: { fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' },
  tableWrapper: { overflowX: 'auto', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff' },
  th: { textAlign: 'left', padding: '0.65rem 0.9rem', background: '#34495e', color: '#fff', fontSize: '0.82rem' },
  row: { borderBottom: '1px solid #eee' },
  td: { padding: '0.6rem 0.9rem', fontSize: '0.88rem', color: '#333' },
  tdCenter: { padding: '0.6rem 0.9rem', fontSize: '0.88rem', color: '#333', textAlign: 'center' },
  badge: { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600, textTransform: 'capitalize' },
  badgeDone: { background: '#cce5ff', color: '#004085' },
  actionRow: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' },
  approveBtn: { padding: '0.25rem 0.65rem', background: '#155724', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' },
  whitelistBtn: { padding: '0.25rem 0.65rem', background: '#004085', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' },
  projectCard: { background: '#fff', borderRadius: '8px', marginBottom: '0.75rem', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', overflow: 'hidden' },
  projectRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.9rem 1.25rem' },
  projectInfo: { display: 'flex', flexDirection: 'column', gap: '0.2rem', cursor: 'pointer', flex: 1 },
  projectName: { fontWeight: 600, fontSize: '1rem', color: '#2c3e50' },
  projectMeta: { fontSize: '0.8rem', color: '#888' },
  projectRight: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  expandBtn: { fontSize: '0.82rem', color: '#2c3e50', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  deleteBtn: { background: 'none', border: '1px solid #c0392b', color: '#c0392b', borderRadius: '4px', padding: '0.25rem 0.6rem', fontSize: '0.8rem', cursor: 'pointer' },
  taskPanel: { borderTop: '1px solid #eee', padding: '1rem', background: '#fafafa' },
  select: { padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid #ccc', fontSize: '0.85rem', maxWidth: '180px' },
  assignBtn: { padding: '0.3rem 0.75rem', background: '#2c3e50', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.82rem' },
};
